import * as React from 'react'
import dayjs from 'dayjs'
import { ColumnDef } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'

import type { IntervalReportSettings } from '@/hooks/useApi/useActiveManagementApi/types'
import { numberWithCommas } from '@/utils/numberWithCommas'

const getUnit = (dataType: IntervalReportSettings['dataType']) =>
    dataType === 'usage' ? 'kWh' : 'kW'

const formatDateForTable = (value: string) => {
    if (!dayjs(value).isValid()) return value
    return dayjs(value).format('MM/DD/YYYY')
}

const formatEndTimeForTable = (value: string) => {
    if (!dayjs(value).isValid()) return value
    const time = value.split('T')[1]
    return time.slice(0, 5)
}

const getDataTypeLabelKey = (
    dataType: IntervalReportSettings['dataType']
): string => {
    switch (dataType) {
        case 'usage':
            return 'active_management.interval.report_settings.data_setup.dataType.usage.label'
        case 'demandAverage':
            return 'active_management.interval.report_settings.data_setup.dataType.demandAverage.label'
        case 'demandMax':
            return 'active_management.interval.report_settings.data_setup.dataType.demandMax.label'
        default:
            return 'active_management.interval.report_settings.data_setup.dataType.usage.label'
    }
}

const formatNumberForCell = (value: number) => numberWithCommas(value, 2)

type UseColumnsArgs = {
    dataGrouping: 'aggregate' | 'compare'
    reportSettings: IntervalReportSettings
    aggregatedMetersCount: number
    meterIdsToShow: number[]
    meterNameById: Map<number, string>
}

export const useColumns = ({
    dataGrouping,
    reportSettings,
    aggregatedMetersCount,
    meterIdsToShow,
    meterNameById,
}: UseColumnsArgs): ColumnDef<unknown>[] => {
    const { t } = useTranslation('translations')

    return React.useMemo(() => {
        const unit = getUnit(reportSettings.dataType)

        const dateCol: ColumnDef<unknown> = {
            id: 'date',
            accessorKey: 'timestamp',
            header: t('active_management.interval.table.date'),
            sortingFn: 'datetime',
            cell: ({ getValue }) => {
                const value = getValue() as string
                const formatted = formatDateForTable(value)
                return (
                    <div
                        className="overflow-hidden text-ellipsis whitespace-nowrap"
                        title={value}
                    >
                        {formatted}
                    </div>
                )
            },
        }

        const endTimeCol: ColumnDef<unknown> = {
            id: 'endTime',
            accessorKey: 'timestamp',
            header: t('active_management.interval.table.end_time'),
            sortingFn: 'datetime',
            cell: ({ getValue }) => {
                const value = getValue() as string
                const formatted = formatEndTimeForTable(value)
                return (
                    <div
                        className="overflow-hidden text-ellipsis whitespace-nowrap"
                        title={value}
                    >
                        {formatted}
                    </div>
                )
            },
        }

        const groupHeader = `${t(getDataTypeLabelKey(reportSettings.dataType))} (${unit})`

        if (dataGrouping === 'aggregate') {
            const aggregatedCol: ColumnDef<unknown> = {
                id: 'aggregated',
                accessorKey: 'value',
                header: `${aggregatedMetersCount.toString()} ${t(
                    'active_management.interval.table.aggregated_meters',
                    'Aggregated meters'
                )}`,
                enableSorting: true,
                sortingFn: 'basic',
                sortUndefined: 'last',
                cell: ({ getValue }) => {
                    const value = getValue()
                    if (typeof value !== 'number') return '-'
                    return formatNumberForCell(value)
                },
            }

            return [
                dateCol,
                endTimeCol,
                {
                    header: groupHeader,
                    id: 'dataTypeGroup',
                    meta: { keepGroupHeader: true },
                    columns: [aggregatedCol],
                },
            ]
        }

        const meterCols: ColumnDef<unknown>[] = meterIdsToShow.map(
            (meterId) => {
                const key = meterId.toString()
                const name = meterNameById.get(meterId) ?? `Meter ${key}`

                return {
                    id: key,
                    accessorKey: key,
                    header: name,
                    enableSorting: true,
                    sortingFn: 'basic',
                    sortUndefined: 'last',
                    cell: ({ getValue }) => {
                        const value = getValue()
                        if (typeof value !== 'number') return '-'
                        return formatNumberForCell(value)
                    },
                }
            }
        )

        return [
            dateCol,
            endTimeCol,
            {
                header: groupHeader,
                id: 'dataTypeGroup',
                meta: { keepGroupHeader: true },
                columns: meterCols,
            },
        ]
    }, [
        aggregatedMetersCount,
        dataGrouping,
        meterIdsToShow,
        meterNameById,
        reportSettings.dataType,
        t,
    ])
}
